import { useState, useEffect, useRef } from "react";
import { Lock, Clock } from "lucide-react";

const PHASES = [
  { label: "Week 1-2", color: "#ef4444", bgLight: "#fef2f2", impact: "high" },
  { label: "Week 3-4", color: "#f97316", bgLight: "#fff7ed", impact: "medium" },
  { label: "Month 2", color: "#eab308", bgLight: "#fefce8", impact: "low" },
  { label: "Month 3", color: "#22c55e", bgLight: "#f0fdf4", impact: "maintenance" },
];

/* Map issues to timeline phases based on impact */
function mapIssuesToPhases(business, scores, website_analysis, geo_grid) {
  const biz = business || {};
  const wa = website_analysis || {};
  const signals = scores?.signals || [];
  const sig = (key) => signals.find(s => s.key === key);

  const citationSig = sig("citations");
  const postsSig = sig("posts");
  const responseSig = sig("review_response");
  const gpsSig = sig("gps");
  const photoCount = biz.photos?.length || 0;
  const geoKeys = Object.keys(geo_grid || {}).filter(k => k !== "__meta__");
  const avgRanks = geoKeys.map(k => geo_grid[k]?.average_rank).filter(Boolean);
  const bestRank = avgRanks.length > 0 ? Math.min(...avgRanks) : 21;

  const all = [];

  // HIGH IMPACT → Week 1-2
  if (!biz.description) all.push({ phase: 0, task: "Fix GBP Description", time: "10 min" });
  if (photoCount < 10) all.push({ phase: 0, task: `Add ${10 - photoCount} Photos`, time: "20 min" });
  if (citationSig && citationSig.score < 60) all.push({ phase: 0, task: "Fix Citation Inconsistencies", time: "45 min" });

  // MEDIUM IMPACT → Week 3-4
  if (wa.has_schema === false) all.push({ phase: 1, task: "Add Schema Markup", time: "15 min" });
  if (postsSig && postsSig.score < 50) all.push({ phase: 1, task: "Start Weekly GBP Posts", time: "10 min/wk" });
  if (responseSig && responseSig.score < 70) all.push({ phase: 1, task: "Respond to All Reviews", time: "15 min/wk" });

  // LOW IMPACT → Month 2
  if (bestRank > 3) all.push({ phase: 2, task: "Build Local Citations", time: "2-3 hours" });
  if (wa.nap_score != null && wa.nap_score < 100) all.push({ phase: 2, task: "Add NAP to Website", time: "10 min" });

  // Month 3 — maintenance
  all.push({ phase: 3, task: "Monitor Rankings", time: "Ongoing" });
  all.push({ phase: 3, task: "Generate 5+ Reviews/Month", time: "Ongoing" });

  // Ensure at least 1 item per visible phase
  const grouped = [[], [], [], []];
  all.forEach(item => grouped[item.phase].push(item));

  // Backfill if any early phase is empty
  if (grouped[0].length === 0) grouped[0].push({ phase: 0, task: "Optimize GBP Profile", time: "30 min" });
  if (grouped[1].length === 0) grouped[1].push({ phase: 1, task: "Build Online Reviews", time: "Ongoing" });

  return grouped;
}

/* ── Phase dot ── */
function Dot({ color, animated, delay }) {
  return (
    <div style={{
      width: 16, height: 16, borderRadius: "50%",
      background: color, border: "3px solid #fff",
      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      transform: animated ? "scale(1)" : "scale(0)",
      transition: `transform 0.3s ease ${delay}ms`,
      flexShrink: 0,
    }} />
  );
}

/* ── Task card ── */
function TaskCard({ task, color, bgLight }) {
  return (
    <div style={{
      background: bgLight, border: `1px solid ${color}20`,
      borderRadius: 8, padding: "8px 12px", fontSize: 13,
      color: "#374151", lineHeight: 1.4,
    }}>
      <span style={{ fontWeight: 700, color: "#111827" }}>{task.task}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
        <Clock size={10} style={{ color: "#9ca3af" }} />
        <span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>{task.time}</span>
      </div>
    </div>
  );
}

/* ═══ Main Component ═══ */
export default function TimelineSection({ business, scores, website_analysis, geo_grid, hasAccess, onUnlock }) {
  const [animated, setAnimated] = useState(false);
  const ref = useRef(null);
  const phases = mapIssuesToPhases(business, scores, website_analysis, geo_grid);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setAnimated(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const FREE_PHASES = 2; // Show Week 1-2 and Week 3-4 free

  return (
    <section id="roadmap" data-testid="timeline-section" ref={ref} style={{ marginBottom: 40 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", fontFamily: "'JetBrains Mono',monospace", letterSpacing: ".08em" }}>08</span>
          <div style={{ height: 1, flex: 1, background: "#E2E8F0" }} />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", marginBottom: 4, fontFamily: "'Outfit',sans-serif" }}>
          Your 90-Day Roadmap
        </h2>
        <p style={{ fontSize: 13, color: "#64748B", margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>
          Follow this timeline to fix your issues in priority order for maximum ranking improvement.
        </p>
      </div>

      {/* Desktop horizontal timeline */}
      <div className="hidden md:block" style={{ position: "relative" }}>
        {/* Connecting line */}
        <div style={{
          position: "absolute", top: 7, left: 20, right: 20, height: 2,
          background: "#e5e7eb", zIndex: 0,
        }}>
          <div style={{
            height: "100%", background: "linear-gradient(90deg, #ef4444, #f97316, #eab308, #22c55e)",
            width: animated ? "100%" : "0%",
            transition: "width 1.2s ease-out 0.3s",
            borderRadius: 1,
          }} />
        </div>

        {/* Phases grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, position: "relative", zIndex: 1 }}>
          {PHASES.map((phase, pi) => {
            const isLocked = !hasAccess && pi >= FREE_PHASES;
            const tasks = phases[pi] || [];

            return (
              <div key={pi} data-testid={`timeline-phase-${pi}`} style={{ position: "relative" }}>
                {/* Dot */}
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                  <Dot color={phase.color} animated={animated} delay={300 + pi * 250} />
                </div>

                {/* Phase label */}
                <div style={{
                  textAlign: "center", marginBottom: 12,
                  opacity: animated ? 1 : 0,
                  transform: animated ? "translateY(0)" : "translateY(8px)",
                  transition: `all 0.4s ease ${400 + pi * 250}ms`,
                }}>
                  <span style={{
                    fontSize: 11, fontWeight: 800, color: phase.color,
                    background: phase.bgLight, padding: "3px 10px", borderRadius: 6,
                    letterSpacing: "0.04em",
                  }}>
                    {phase.label}
                  </span>
                </div>

                {/* Task cards */}
                <div style={{
                  display: "flex", flexDirection: "column", gap: 8,
                  filter: isLocked ? "blur(4px)" : "none",
                  pointerEvents: isLocked ? "none" : "auto",
                  opacity: animated ? 1 : 0,
                  transform: animated ? "translateY(0)" : "translateY(12px)",
                  transition: `all 0.4s ease ${500 + pi * 250}ms`,
                }}>
                  {tasks.map((t, ti) => (
                    <TaskCard key={ti} task={t} color={phase.color} bgLight={phase.bgLight} />
                  ))}
                </div>

                {/* Lock overlay for paid phases */}
                {isLocked && (
                  <div style={{
                    position: "absolute", top: 30, left: 0, right: 0, bottom: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    zIndex: 3,
                  }}>
                    <Lock size={18} style={{ color: "#9ca3af" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile vertical timeline */}
      <div className="block md:hidden" style={{ position: "relative", paddingLeft: 28 }}>
        {/* Vertical line */}
        <div style={{
          position: "absolute", top: 0, left: 7, bottom: 0, width: 2,
          background: "#e5e7eb",
        }}>
          <div style={{
            width: "100%",
            background: "linear-gradient(180deg, #ef4444, #f97316, #eab308, #22c55e)",
            height: animated ? "100%" : "0%",
            transition: "height 1.2s ease-out 0.3s",
          }} />
        </div>

        {PHASES.map((phase, pi) => {
          const isLocked = !hasAccess && pi >= FREE_PHASES;
          const tasks = phases[pi] || [];
          return (
            <div key={pi} style={{ position: "relative", marginBottom: 24, paddingLeft: 8 }}>
              {/* Dot on line */}
              <div style={{ position: "absolute", left: -28, top: 2 }}>
                <Dot color={phase.color} animated={animated} delay={300 + pi * 250} />
              </div>

              <span style={{
                fontSize: 11, fontWeight: 800, color: phase.color,
                background: phase.bgLight, padding: "3px 10px", borderRadius: 6,
                display: "inline-block", marginBottom: 8,
              }}>
                {phase.label}
              </span>

              <div style={{
                display: "flex", flexDirection: "column", gap: 8,
                filter: isLocked ? "blur(4px)" : "none",
              }}>
                {tasks.map((t, ti) => (
                  <TaskCard key={ti} task={t} color={phase.color} bgLight={phase.bgLight} />
                ))}
              </div>

              {isLocked && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Lock size={18} style={{ color: "#9ca3af" }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Unlock banner for locked phases */}
      {!hasAccess && (
        <div data-testid="timeline-unlock" style={{
          background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12,
          padding: "16px 24px", textAlign: "center", marginTop: 16,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        }}>
          <Lock size={14} style={{ color: "#6b7280" }} />
          <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 600 }}>
            Unlock your complete 90-day roadmap
          </span>
          <button
            onClick={onUnlock}
            data-testid="timeline-unlock-btn"
            style={{
              background: "#111827", color: "#fff", fontWeight: 800, fontSize: 12,
              padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
              marginLeft: 4,
            }}
          >
            Unlock — $27
          </button>
        </div>
      )}
    </section>
  );
}
